/**
 * Official SOR entity name from Sundhedsdatastyrelsen's public search.
 * Used when a CareCommunication organisation has a SOR id and no name.
 */

const SOR_ENTITY_SEARCH_URL =
  'https://sorbrowser.sundhedsdatastyrelsen.dk/search/sor-browser/_search';

export function isSorId(value: string): boolean {
  return /^\d{8,20}$/.test(value);
}

export function sorEntitySearchBody(sorId: string): {
  size: number;
  query: {
    bool: {
      must: Array<{ term: Record<string, string> }>;
    };
  };
} {
  return {
    size: 1,
    query: {
      bool: {
        must: [{ term: { 'SorId.keyword': sorId } }, { term: { 'IsLatest.keyword': 'true' } }],
      },
    },
  };
}

export function sorEntityNameFromSearch(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null;
  const hits = (payload as { hits?: { hits?: unknown[] } }).hits?.hits;
  const source = (hits?.[0] as { _source?: { EntityName?: unknown } } | undefined)?._source;
  if (typeof source?.EntityName !== 'string') return null;
  const name = source.EntityName.trim();
  return name || null;
}

export async function fetchSorEntityName(sorId: string): Promise<string | null> {
  if (!isSorId(sorId)) return null;
  let response: Response;
  try {
    response = await fetch(SOR_ENTITY_SEARCH_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(sorEntitySearchBody(sorId)),
    });
  } catch {
    return null;
  }
  if (!response.ok) return null;
  try {
    return sorEntityNameFromSearch(await response.json());
  } catch {
    return null;
  }
}

/** Name from the letter, otherwise the name looked up from SOR. */
export function organizationDisplayName(
  bundleName: string | null,
  lookedUpName: string | null | undefined,
): string | null {
  return bundleName ?? lookedUpName ?? null;
}

/** The SOR id of a label that is only `SOR <id>`, otherwise null. */
export function sorIdFromOrganizationLabel(label: string | null | undefined): string | null {
  const match = /^SOR (\d{8,20})$/.exec(label?.trim() ?? '');
  return match ? match[1] : null;
}

/**
 * A stored organisation name that is only a SOR id is not a name. The letter
 * header looks that id up; Beskedsegmenter uses the same fields.
 */
export function organizationFieldsFromStored(
  name: string | null | undefined,
  sorId: string | null | undefined,
): { name: string | null; sorId: string | null } {
  const writtenName = name?.trim() || null;
  const writtenSor = sorId?.trim() || null;
  if (!writtenName) return { name: null, sorId: writtenSor };
  const fromLabel = sorIdFromOrganizationLabel(writtenName);
  if (fromLabel) return { name: null, sorId: writtenSor ?? fromLabel };
  if (isSorId(writtenName)) return { name: null, sorId: writtenSor ?? writtenName };
  return { name: writtenName, sorId: writtenSor };
}

/**
 * A signature labelled only with `SOR <id>` shows the looked-up name of that
 * same sender or receiver. Any other label stays as it is.
 */
export function signatureOrganizationName(
  label: string | null,
  parties: Array<{ sorId: string | null; name: string | null }>,
): string | null {
  if (!label) return null;
  for (const party of parties) {
    if (!party.sorId || !party.name) continue;
    if (label === `SOR ${party.sorId}`) return party.name;
  }
  return label;
}
